import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import type { Lab, Course, UserProgressRecord } from "../services/api";
import { apiService } from "../services/api";
import LockWrap from "../components/LockWrap";
import "../css/Labs.css";
import { usePaywall } from "../hooks/usePaywall";
import Paywall from "../components/Paywall";
import { useAuth } from "../hooks/useAuth";

export const Route = createFileRoute("/labs/")({
  component: LabsIndex,
});

interface LabEntry {
  courseId: string;
  courseTitle: string;
  lab: Lab;
  progress?: UserProgressRecord;
}

function LabsIndex() {
  const [filter, setFilter] = useState<"all" | "beginner" | "intermediate" | "advanced" | "free" | "premium">("all");
  const [animateCards, setAnimateCards] = useState(false);
  const [entries, setEntries] = useState<LabEntry[]>([]);
  const [courses, setCourses] = useState<Record<string, Course>>({});
  const [progressMap, setProgressMap] = useState<Record<string, UserProgressRecord>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { isPaywallVisible, setIsPaywallVisible } = usePaywall();
  const { user } = useAuth();
  
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const [coursesData, userProgress] = await Promise.all([
          apiService.getCoursesAsRecord(),
          apiService.getUserProgress().catch(() => [] as UserProgressRecord[])
        ]);
        
        setCourses(coursesData);
        
        const progressByLab: Record<string, UserProgressRecord> = {};
        userProgress.forEach((record) => {
          progressByLab[record.labId.toString()] = record;
        });
        setProgressMap(progressByLab);
        
        // Create entries array from labs data
        const labEntries: LabEntry[] = [];
        Object.values(coursesData).forEach((course) => {
          (course.labs ?? []).forEach((lab) => {
            labEntries.push({
              courseId: course.id,
              courseTitle: course.title,
              lab,
              progress: progressByLab[lab.id]
            });
          });
        });
        
        setEntries(labEntries);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load data');
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, []);

  const filteredEntries =
    filter === "all"
      ? entries
      : filter === "free"
      ? entries.filter(({ lab }) => {
          const isLocked = lab.isPremium && user?.subscription !== 'premium';
          return lab && !isLocked;
        })
      : filter === "premium"
      ? entries.filter(({ lab }) => {
          const isLocked = lab.isPremium && user?.subscription !== 'premium';
          return isLocked;
        })
      : entries.filter(({ lab }) => lab?.level === filter);

  useEffect(() => {
    setAnimateCards(true);
  }, []);

  const levelCounts = {
    beginner: entries.filter(({ lab }) => lab?.level === "beginner").length,
    intermediate: entries.filter(({ lab }) => lab?.level === "intermediate").length,
    advanced: entries.filter(({ lab }) => lab?.level === "advanced").length,
    free: entries.filter(({ lab }) => {
      const isLocked = lab.isPremium && user?.subscription !== 'premium';
      return lab && !isLocked;
    }).length,
    premium: entries.filter(({ lab }) => {
      const isLocked = lab.isPremium && user?.subscription !== 'premium';
      return isLocked;
    }).length,
  };

  const completedLabsFromProgress = Object.values(progressMap).filter((record) => record.completed).length;
  const inProgressLabsCount = Object.values(progressMap).filter((record) => !record.completed && record.progress > 0).length;
  const isMobileView = typeof window !== "undefined" && window.innerWidth <= 640;

  if (loading) {
    return (
      <div style={{ background: "#0a0f1a", color: "#f5f5f5", minHeight: "100vh", padding: 40 }}>
        <div style={{ textAlign: 'center', padding: 40 }}>
          <div style={{ fontSize: '1.2rem', opacity: 0.8 }}>Loading labs...</div>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div style={{ background: "#0a0f1a", color: "#f5f5f5", minHeight: "100vh", padding: 40 }}>
        <div style={{
          background: 'rgba(239, 68, 68, 0.1)',
          border: '1px solid rgba(239, 68, 68, 0.2)',
          color: '#ef4444',
          padding: 20,
          borderRadius: 8,
          marginBottom: 20
        }}>
          <h3>Error Loading Labs</h3>
          <p>{error}</p>
          <p style={{ opacity: 0.8, fontSize: '0.9rem' }}>Please check if the API server is running.</p>
        </div>
      </div>
    );
  }
  
  return (
    <div
      style={{ background: "#0a0f1a", color: "#f5f5f5", minHeight: "100vh", position: "relative" }}
    >
      {isPaywallVisible && <Paywall onClose={() => setIsPaywallVisible(false)} />}
      {/* Background Effects */}
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background:
            "radial-gradient(circle at 20% 30%, rgba(34, 211, 238, 0.1) 0%, transparent 50%), " +
            "radial-gradient(circle at 80% 70%, rgba(74, 222, 128, 0.1) 0%, transparent 50%), " +
            "radial-gradient(circle at 50% 50%, rgba(167, 139, 250, 0.05) 0%, transparent 70%)",
          zIndex: 0,
          pointerEvents: "none",
        }}
      />

      <div
        style={{
          position: "relative",
          zIndex: 1,
          padding: "40px clamp(20px, 5vw, 80px)",
          maxWidth: "1400px",
          margin: "0 auto",
        }}
      >
        {/* Navigation */}
        <div style={{ marginBottom: 40 }}>
          <Link className="link-button" to="/courses">
            ← Back to Courses
          </Link>
        </div>

        {/* Header Section */}
        <div style={{ marginBottom: 50, textAlign: "center", padding: "0 20px" }}>
          <h1
            style={{
              fontSize: "clamp(2.5rem, 6vw, 4rem)",
              marginBottom: 20,
              background: "linear-gradient(135deg, #ffffff, var(--color-secondary), var(--color-primary), var(--color-pink))",
              backgroundSize: "200% 200%",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
              animation: "gradientShift 4s ease-in-out infinite alternate",
              fontWeight: 900,
              letterSpacing: "-0.02em",
            }}
          >
            🧪 All Labs
          </h1>
          <p
            style={{ fontSize: "clamp(1.1rem, 2.5vw, 1.4rem)", opacity: 0.9, maxWidth: "600px", margin: "0 auto", lineHeight: 1.6 }}
          >
            Hands-on exercises to apply concepts from each course. Build your skills through interactive learning.
          </p>

          {/* Free Labs Badge */}
          {(user?.subscription !== 'premium') ?
          (<div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "8px",
              marginTop: "1rem",
              padding: "8px 16px",
              background: "linear-gradient(135deg, var(--color-primary), var(--color-secondary), var(--color-purple))",
              borderRadius: "20px",
              fontSize: "0.9rem",
              fontWeight: "600",
              color: "#0a0f1a",
            }}
          >
            ✨ {levelCounts.free} Free Labs Available!
          </div>
          ): ""}
          {/* Stats */}
          <div style={{ display: "flex", justifyContent: "center", gap: "2rem", marginTop: "2rem", flexWrap: "wrap" }}>
            <div style={{ background: "rgba(255, 255, 255, 0.05)", border: "1px solid rgba(74, 222, 128, 0.3)", borderRadius: "12px", padding: "12px 20px", textAlign: "center" }}>
              <div style={{ fontSize: "1.5rem", fontWeight: "bold", color: "#4ade80" }}>{entries.length}</div>
              <div style={{ fontSize: "0.9rem", opacity: 0.8 }}>Total Labs</div>
            </div>
            <div style={{ background: "rgba(255, 255, 255, 0.05)", border: "1px solid rgba(34, 211, 238, 0.3)", borderRadius: "12px", padding: "12px 20px", textAlign: "center" }}>
              <div style={{ fontSize: "1.5rem", fontWeight: "bold", color: "#22d3ee" }}>{Object.keys(courses).length}</div>
              <div style={{ fontSize: "0.9rem", opacity: 0.8 }}>Courses</div>
            </div>
            <div style={{ background: "var(--color-pink-soft)", border: "1px solid var(--color-pink-border)", borderRadius: "12px", padding: "12px 20px", textAlign: "center" }}>
              <div style={{ fontSize: "1.5rem", fontWeight: "bold", color: "var(--color-pink)" }}>{completedLabsFromProgress}</div>
              <div style={{ fontSize: "0.9rem", opacity: 0.8 }}>Labs Completed</div>
            </div>
            <div style={{ background: "rgba(255, 255, 255, 0.05)", border: "1px solid rgba(167, 139, 250, 0.3)", borderRadius: "12px", padding: "12px 20px", textAlign: "center" }}>
              <div style={{ fontSize: "1.5rem", fontWeight: "bold", color: "var(--color-purple)" }}>{inProgressLabsCount}</div>
              <div style={{ fontSize: "0.9rem", opacity: 0.8 }}>In Progress</div>
            </div>
          </div>
        </div>

        {/* Filter Buttons */}
        {(user?.subscription !== 'premium') ?
        (<div style={{ display: "flex", justifyContent: "center", gap: "12px", marginBottom: 40, flexWrap: "wrap" }}>
          {[
            { key: "all", label: "All Labs", count: entries.length },
            { key: "free", label: "🎁 Free", count: levelCounts.free, highlight: true },
            { key: "premium", label: "👑 Premium", count: levelCounts.premium },
            { key: "beginner", label: "Beginner", count: levelCounts.beginner },
            { key: "intermediate", label: "Intermediate", count: levelCounts.intermediate },
            { key: "advanced", label: "Advanced", count: levelCounts.advanced },
          ].map(({ key, label, count, highlight }) => (
            <button
              key={key}
              onClick={() => setFilter(key as any)}
              style={{
                padding: "10px 16px",
                borderRadius: "20px",
                border:
                  filter === key ? "2px solid #22d3ee" : highlight ? "1px solid rgba(74, 222, 128, 0.5)" : "1px solid rgba(255, 255, 255, 0.2)",
                background:
                  filter === key ? "rgba(34, 211, 238, 0.2)" : highlight ? "rgba(74, 222, 128, 0.1)" : "rgba(255, 255, 255, 0.05)",
                color: filter === key ? "#22d3ee" : highlight ? "#4ade80" : "#e2e8f0",
                cursor: "pointer",
                transition: "all 0.3s ease",
                fontWeight: filter === key ? "600" : highlight ? "500" : "400",
                fontSize: "0.9rem",
                display: "flex",
                alignItems: "center",
                gap: "6px",
                animation: highlight && filter !== key ? "pulse 2s infinite" : "none",
              }}
              onMouseEnter={(e) => {
                if (filter !== key) {
                  e.currentTarget.style.background = "rgba(255, 255, 255, 0.1)";
                  e.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.3)";
                }
              }}
              onMouseLeave={(e) => {
                if (filter !== key) {
                  e.currentTarget.style.background = "rgba(255, 255, 255, 0.05)";
                  e.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.2)";
                }
              }}
            >
              {label}
              <span style={{ background: "rgba(255, 255, 255, 0.2)", borderRadius: "10px", padding: "2px 6px", fontSize: "0.75rem", fontWeight: "600" }}>
                {count}
              </span>
            </button>
          ))}
        </div>) : ""
      } 

        {/* Labs Sections */}
        {filter === "all" || filter === "free" || filter === "premium" ? (
          <div>
            {/* Free Section */}
            {(filter === "all" || filter === "free") && entries.filter(({ lab }) => {
              const isLocked = lab.isPremium && user?.subscription !== 'premium';
              return !isLocked;
            }).length > 0 && (
              <Section title={`🎁 ${user?.subscription === 'premium' ? 'Your Labs' : 'Free'}`}>
                <div
                  className="lab-grid"
                  style={{ opacity: animateCards ? 1 : 0, transform: animateCards ? "translateY(0)" : "translateY(20px)", transition: "all 0.6s ease-out" }}
                >
                  {entries
                    .filter(({ lab }) => {
                      const isLocked = lab.isPremium && user?.subscription !== 'premium';
                      return !isLocked;
                    })
                    .map(({ courseId, courseTitle, lab, progress }, index) => (
                      <LabCard key={courseId + lab.id} courseTitle={courseTitle} lab={lab} index={index} free progress={progress} />
                    ))}
                </div>
              </Section>
            )}

            {/* Premium Section */}
            {(filter === "all" || filter === "premium") && entries.filter(({ lab }) => {
              const isLocked = lab.isPremium && user?.subscription !== 'premium';
              return isLocked;
            }).length > 0 && (
              <Section title="👑 Premium" premium>
                <div
                  className="lab-grid premium-grid"
                  style={{
                    opacity: animateCards ? 1 : 0,
                    transform: animateCards ? "translateY(0)" : "translateY(20px)",
                    transition: "all 0.6s ease-out",
                    gap: isMobileView ? 36 : undefined,
                  }}
                >
                  {entries
                    .filter(({ lab }) => {
                      const isLocked = lab.isPremium && user?.subscription !== 'premium';
                      return isLocked;
                    })
                    .map(({ courseId, courseTitle, lab, progress }, index) => {
                      const isLocked = lab.isPremium && user?.subscription !== 'premium';
                      return (
                        <LabCard key={courseId + lab.id} courseTitle={courseTitle} lab={lab} index={index} progress={progress} onClick={() => isLocked && setIsPaywallVisible(true)} />
                      )
                    })}
                </div>
              </Section>
            )}
          </div>
        ) : (
          // Difficulty-only filter
          <div
            className="lab-grid"
            style={{ opacity: animateCards ? 1 : 0, transform: animateCards ? "translateY(0)" : "translateY(20px)", transition: "all 0.6s ease-out" }}
          >
            {filteredEntries.map(({ courseId, courseTitle, lab, progress }, index) => {
                const isLocked = lab.isPremium && user?.subscription !== 'premium';
                return (
                  <LabCard key={courseId + lab.id} courseTitle={courseTitle} lab={lab} index={index} free={!isLocked} progress={progress} onClick={() => isLocked && setIsPaywallVisible(true)} />
                )
            })}
          </div>
        )}
      </div>

      {/* CSS animations */}
      <style>{`
        @keyframes gradientShift { 0% { background-position: 0% 50%; } 100% { background-position: 100% 50%; } }
        @keyframes pulse { 0%, 100% { opacity: 1; transform: scale(1); } 50% { opacity: 0.8; transform: scale(1.02); } }
      `}</style>
    </div>
  );
}

function Section({ title, premium, children }: { title: string; premium?: boolean; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: "60px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "15px", marginBottom: "30px" }}>
        <h2
          style={{
            fontSize: "1.8rem",
            background: premium ? "linear-gradient(135deg, var(--color-gold-alt), var(--color-brown))" : "linear-gradient(135deg, var(--color-primary), var(--color-secondary), var(--color-purple))",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
            margin: 0,
          }}
        >
          {title}
        </h2>
        <div
          style={{ height: "2px", flex: 1, background: premium ? "linear-gradient(90deg, rgba(251, 191, 36, 0.3), transparent)" : "linear-gradient(90deg, rgba(74, 222, 128, 0.3), transparent)" }}
        />
      </div>
      {children}
    </div>
  );
}

function LabCard({ courseTitle, lab, index, free, onClick, progress }: { courseTitle: string; lab: { id: string; title: string; description?: string; isPremium?: boolean; level?: string; durationMins?: number; points?: number }; index: number; free?: boolean; onClick?: () => void; progress?: UserProgressRecord }) {
  const { user } = useAuth();
  const isLocked = lab.isPremium && user?.subscription !== 'premium';
  const completed = progress?.completed;
  const progressPercent = progress?.progress ?? 0;
  const isMobileCard = typeof window !== "undefined" && window.innerWidth <= 640;
  return (
    <div className="lab-card-wrapper" style={{ position: "relative", marginBottom: isMobileCard && isLocked ? 24 : 0 }} onClick={onClick}>
      {completed && (
        <div
          style={{
            position: "absolute",
            top: "-12px",
            left: "12px",
            background: "linear-gradient(135deg, #4ade80, #16a34a)",
            color: "#0a0f1a",
            padding: "4px 12px",
            borderRadius: "12px",
            fontSize: "0.75rem",
            fontWeight: "700",
            boxShadow: "0 4px 12px rgba(74, 222, 128, 0.3)",
            zIndex: 10,
          }}
        >
          ✅ Completed
        </div>
      )}
      {free && user?.subscription !== 'premium' && (
        <div
          style={{
            position: "absolute",
            top: "-12px",
            right: "12px",
            background: "linear-gradient(135deg, var(--color-primary), var(--color-secondary), var(--color-purple))",
            color: "#0a0f1a",
            padding: "4px 12px",
            borderRadius: "12px",
            fontSize: "0.75rem",
            fontWeight: "700",
            boxShadow: "0 4px 12px rgba(74, 222, 128, 0.3)",
            zIndex: 10,
          }}
        >
          FREE
        </div>
      )}
      {isLocked && (
        <div
          className="lab-card-lock"
          style={{
            position: "absolute",
            top: "-12px",
            right: "12px",
            background: "linear-gradient(135deg, var(--color-gold-alt), var(--color-brown))",
            color: "#0a0f1a",
            padding: "4px 12px",
            borderRadius: "12px",
            fontSize: "0.75rem",
            fontWeight: "700",
            boxShadow: "0 4px 12px rgba(251, 191, 36, 0.3)",
            zIndex: 20,
            display: "flex",
            alignItems: "center",
            gap: "4px"
          }}
        >
          🔒 Premium
        </div>
      )}
      <div className="lab-card fade-in" style={{ animationDelay: `${index * 0.1}s`, position: "relative", overflow: "hidden" }}>
        <span className="lab-card-bar" aria-hidden="true" />
        <LockWrap isLocked={isLocked}>
          <div className="lab-card-inner" style={{ height: "100%" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "8px" }}>
              <h3 style={{ paddingRight: isLocked ? "10px" : "50px" }}>{lab.title}</h3>
            </div>
            <p className="muted" style={{ marginBottom: "16px" }}>
              📚 {courseTitle} • 📋 {(lab as any).steps?.length || 0} steps
            </p>
            {progress && (
              <div style={{ marginBottom: 16 }}>
                <div style={{
                  height: 6,
                  borderRadius: 999,
                  background: "rgba(255, 255, 255, 0.08)",
                  overflow: "hidden"
                }}>
                  <div
                    style={{
                      height: "100%",
                      width: `${Math.min(100, progressPercent)}%`,
                      background: completed ? "linear-gradient(90deg, #4ade80, #22d3ee)" : "linear-gradient(90deg, #22d3ee, #38bdf8)",
                      transition: "width 0.4s ease"
                    }}
                  />
                </div>
                <div style={{ fontSize: "0.75rem", opacity: 0.75, marginTop: 6 }}>
                  {completed ? "Completed" : `${Math.min(100, progressPercent)}% complete`}
                </div>
              </div>
            )}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "auto" }}>
              {!isLocked ? (
                <Link
                  to="/labs/$id"
                  params={{ id: lab.id }}
                  style={{ color: "#22d3ee", textDecoration: "none", fontWeight: 500, transition: "all 0.3s ease", cursor: "pointer" }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = "#4ade80";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = "#22d3ee";
                  }}
                >
                  Open Lab →
                </Link>
              ) : (
                <span style={{ fontSize: "0.8rem", color: "var(--color-gold-alt)", display: "flex", alignItems: "center", gap: "4px" }}>🔒 Premium</span>
              )}
            </div>
          </div>
        </LockWrap>
      </div>
      {isLocked && (
        <>
          <svg
            className="lab-card-lock"
            viewBox="0 0 24 24"
            width="48"
            height="48"
            aria-hidden="true"
            style={{
              position: "absolute",
              top: "0px",
              left: "50%",
              transform: "translate(-50%, -50%)",
              zIndex: 50,
              color: "white",
              filter: "drop-shadow(0 4px 12px rgba(0, 0, 0, 0.5))",
              pointerEvents: "none"
            }}
          >
            <path
              fill="currentColor"
              d="M17 8h-1V6a4 4 0 10-8 0v2H7a2 2 0 00-2 2v8a2 2 0 002 2h10a2 2 0 002-2v-8a2 2 0 00-2-2zm-6 8a1 1 0 11-2 0 1 1 0 012 0zm3-8H10V6a2 2 0 014 0v2z"
            />
          </svg>
          <button
            className="lab-card-cta"
            onClick={onClick}
            style={{
              position: "absolute",
              bottom: isMobileCard ? "-8px" : "0px",
              left: "50%",
              transform: isMobileCard ? "translate(-50%, 0)" : "translate(-50%, 50%)",
              zIndex: 50,
              padding: isMobileCard ? "0.75rem 1.75rem" : "0.6rem 1.5rem",
              borderRadius: 10,
              border: "none",
              cursor: "pointer",
              fontWeight: 600,
              background: "linear-gradient(135deg, var(--color-secondary), var(--color-primary), var(--color-purple))",
              color: "#0a0f1a",
              boxShadow: "0 4px 12px rgba(34, 211, 238, 0.4)",
              fontSize: "0.9rem"
            }}
          >
            Join Now
          </button>
        </>
      )}
    </div>
  );
}
